/**
 * The Watcher's eye — the Pantocrator, and the honest disclosure.
 *
 * In a domed church the Pantocrator occupies the ceiling and looks down on
 * everyone below. That is the position of surveillance in sacred architecture,
 * and it is the right place for the thing that is actually watching. So the
 * indicator that frames are being sent off-device is not a status chip in a
 * corner; it is an eye that opens overhead, blinks, and closes.
 *
 * ⚠️ This is a consent-critical element. It MUST be driven by the real request
 * lifecycle — `watcherLooking` is set around the actual fetch in
 * `session/runtime.ts` — and it must never be simulated, decorative, or hidden
 * inside the controls drawer. It also renders whether or not the scan overlay is
 * toggled off. See STATUS §9.
 *
 * When it has named something in the frame, the name is shown beside the line.
 * That is the proof of presence: a line about "the chair by the window" could
 * not have been written without looking.
 */

import { useEffect, useRef, useState } from 'react';
import { Sun } from './Sun.js';
import { useStore } from '../../state/store.js';
import { runtime } from '../../session/runtime.js';

/** How long the eye lingers after the request finishes, so it is not a flicker. */
const LINGER_MS = 1100;

export function WatcherEye() {
  const consented = useStore((s) => s.watcherConsent);
  const looking = useStore((s) => s.watcherLooking);
  const blink = useStore((s) => s.watcherBlink);
  const [presence, setPresence] = useState(0);

  // Fade in immediately on looking, out after a linger. Animated in a rAF rather
  // than by CSS so it shares the sun's clock and cannot desync from the blink.
  const target = useRef(0);
  target.current = looking ? 1 : 0;

  // Without consent there is nothing to disclose and no frame will ever be sent,
  // so there is no loop to run. The loop also tells the runtime the eye is on
  // screen: the Watcher uploads a frame only while it is (runtime.onWatch).
  useEffect(() => {
    if (!consented) return;
    runtime.setWatcherEyeMounted(true);
    let raf = 0;
    let last = performance.now();
    let out = 0;
    let holdUntil = 0;

    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (target.current > 0) holdUntil = now + LINGER_MS;
      const want = now < holdUntil ? 1 : 0;
      // Rise fast, fall slow: an eye that snaps shut reads as a glitch.
      const rate = want > out ? 3.2 : 0.9;
      out += (want - out) * Math.min(1, dt * rate);
      setPresence(out < 0.002 ? 0 : out);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      runtime.setWatcherEyeMounted(false);
    };
  }, [consented]);

  if (!consented) return null;

  return (
    <div className="watcher-eye" aria-live="polite">
      <Sun
        presence={presence}
        gain={0.85}
        sizeFraction={0.62}
        centreY={0.17}
        blinkEvery={2.4}
        blinkNow={blink}
        className="watcher-eye-canvas"
      />
      {presence > 0.15 && (
        <p className="watcher-eye-label">
          {looking ? 'it is looking' : 'it looked'}
        </p>
      )}
      {/* The line itself is NOT repeated here. `Directive.tsx` types it in
          gold, and this component rendered the same string a second time in
          screen space a few pixels away — two subtitles for one utterance,
          which is what field testing on 2026-08-28 actually saw. The eye's job
          is disclosure ("it is looking"), not transcription. */}
    </div>
  );
}
